import React, { useEffect, useMemo, useState } from "react";
import { apptApi, apiError } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Modal, Button, Field } from "../../Components/UI/ui";
import { SERVICE_TYPES, cap, toDateInput, toTimeInput, todayStr, notificationLine } from "../../lib/lab";
import PatientPicker from "./PatientPicker";
import SkuLines from "./SkuLines";

const emptyAddress = { addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "" };

// Book a new appointment (§3.1) or edit one that is still REQUESTED/CONFIRMED (§3.5).
const AppointmentForm = ({ open, onClose, onSaved, appointment, initialDate }) => {
  const { skus, loadSkus, toast } = useApp();
  const editing = !!appointment;

  const [patient, setPatient] = useState(null);
  const [serviceType, setServiceType] = useState("pathology");
  const [lines, setLines] = useState([]);
  const [date, setDate] = useState(initialDate || todayStr());
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(15);
  const [collectionType, setCollectionType] = useState("at_centre");
  const [address, setAddress] = useState(emptyAddress);
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [remember, setRemember] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    loadSkus();
    setErr(null);
    if (appointment) {
      setPatient({ _id: appointment.patientId, name: { firstName: appointment.patientName }, contact: { phoneNumber: appointment.patientPhone } });
      setServiceType(appointment.serviceType);
      setLines((appointment.items || []).map((it) => ({
        labSkuId: it.labSkuId, code: it.labSkuCode, name: it.name, kind: it.kind, category: it.category,
        unitPrice: it.unitPrice, tax: { cgst: it.taxPercent || 0 }, homeCollectionAvailable: it.homeCollectionAvailable,
        qty: it.qty || 1, discount: it.discount || 0,
      })));
      setDate(toDateInput(appointment.scheduledAt));
      setTime(toTimeInput(appointment.scheduledAt));
      setDuration(appointment.durationMinutes || 15);
      setCollectionType(appointment.collectionType || "at_centre");
      setAddress({ ...emptyAddress, ...(appointment.homeCollection?.address || {}) });
      setWindowStart(appointment.homeCollection?.windowStart ? toTimeInput(appointment.homeCollection.windowStart) : "");
      setWindowEnd(appointment.homeCollection?.windowEnd ? toTimeInput(appointment.homeCollection.windowEnd) : "");
      setNotes(appointment.notes || "");
      setRemember((appointment.thingsToRemember || []).join(", "));
    } else {
      setPatient(null); setServiceType("pathology"); setLines([]);
      setDate(initialDate || todayStr()); setTime("09:00"); setDuration(15);
      setCollectionType("at_centre"); setAddress(emptyAddress); setWindowStart(""); setWindowEnd("");
      setNotes(""); setRemember("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment]);

  // Home collection only for pathology and only if every line supports it
  const homeAllowed = serviceType === "pathology" && lines.length > 0 && lines.every((l) => l.homeCollectionAvailable);
  useEffect(() => {
    if (collectionType === "home" && !homeAllowed) setCollectionType("at_centre");
  }, [homeAllowed, collectionType]);

  const changeService = (t) => {
    setServiceType(t);
    setLines((ls) => ls.filter((l) => skus.find((k) => k._id === l.labSkuId)?.serviceType === t));
  };

  const scheduledAt = useMemo(() => new Date(`${date}T${time || "09:00"}:00`), [date, time]);

  const submit = async () => {
    setErr(null);
    if (!patient?._id) return setErr("Select or create a patient");
    if (lines.length === 0) return setErr("Add at least one item");
    if (!date || !time) return setErr("Pick a date and time");

    const body = {
      serviceType,
      scheduledAt: scheduledAt.toISOString(),
      durationMinutes: Number(duration) || 15,
      items: lines.map((l) => ({ labSkuId: l.labSkuId, qty: l.qty, discount: l.discount })),
      collectionType,
      notes: notes.trim(),
      thingsToRemember: remember.split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (collectionType === "home") {
      if (!address.addressLine1.trim()) return setErr("Home collection needs an address");
      body.homeCollection = {
        address,
        windowStart: windowStart ? new Date(`${date}T${windowStart}:00`).toISOString() : undefined,
        windowEnd: windowEnd ? new Date(`${date}T${windowEnd}:00`).toISOString() : undefined,
      };
    }

    setSaving(true);
    try {
      let saved;
      if (editing) {
        saved = await apptApi.update(appointment._id, body);
        toast.success("Booking updated");
      } else {
        saved = await apptApi.create({ patientId: patient._id, ...body });
        toast.success("Booking created");
        // The server WhatsApps the patient a confirmation as the booking is made
        const c = saved?.confirmation;
        if (c?.status === "sent") toast.success(notificationLine(c));
        else if (c?.status === "failed") toast.error(`${notificationLine(c)} — use "Retry confirmation" on the booking.`);
        const missing = saved?.results?.missingTemplates;
        if (missing?.length) {
          toast.error(`No result sheet for: ${missing.map((m) => m.name || m.skuName || m).join(", ")} — link a test definition to these catalogue items`);
        }
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit booking · ${appointment.patientName}` : "New booking"}
      footer={
        <>
          {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>{editing ? "Save changes" : "Book appointment"}</Button>
        </>
      }
    >
      <div className="lab-section">
        <h4>Patient</h4>
        {editing ? (
          <div>{appointment.patientName} <span className="lab-muted">· {appointment.patientPhone}</span></div>
        ) : (
          <PatientPicker value={patient} onChange={setPatient} />
        )}
      </div>

      <div className="lab-section">
        <h4>
          Tests &amp; scans
          <span className="lab-row">
            {SERVICE_TYPES.map((t) => (
              <label key={t} className="lab-check">
                <input type="radio" name="serviceType" checked={serviceType === t} onChange={() => changeService(t)} />
                {cap(t)}
              </label>
            ))}
          </span>
        </h4>
        <SkuLines skus={skus} serviceType={serviceType} lines={lines} onChange={setLines} />
      </div>

      <div className="lab-section">
        <h4>Schedule</h4>
        <div className="lab-grid cols-3">
          <Field label="Date"><input type="date" className="lab-input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Time"><input type="time" className="lab-input" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          <Field label="Duration (min)"><input type="number" min="5" step="5" className="lab-input" value={duration} onChange={(e) => setDuration(e.target.value)} /></Field>
        </div>
      </div>

      <div className="lab-section">
        <h4>
          Collection
          <span className="lab-row">
            <label className="lab-check"><input type="radio" checked={collectionType === "at_centre"} onChange={() => setCollectionType("at_centre")} /> At centre</label>
            <label className="lab-check" title={homeAllowed ? "" : "Home collection needs pathology items that all allow it"}>
              <input type="radio" disabled={!homeAllowed} checked={collectionType === "home"} onChange={() => setCollectionType("home")} /> Home collection
            </label>
          </span>
        </h4>
        {collectionType === "home" ? (
          <div className="lab-grid cols-3">
            <Field label="Address line 1" className="span2"><input className="lab-input" value={address.addressLine1} onChange={(e) => setAddress({ ...address, addressLine1: e.target.value })} /></Field>
            <Field label="Address line 2"><input className="lab-input" value={address.addressLine2} onChange={(e) => setAddress({ ...address, addressLine2: e.target.value })} /></Field>
            <Field label="City"><input className="lab-input" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} /></Field>
            <Field label="State"><input className="lab-input" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} /></Field>
            <Field label="Postal code"><input className="lab-input" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} /></Field>
            <Field label="Window from"><input type="time" className="lab-input" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} /></Field>
            <Field label="Window to"><input type="time" className="lab-input" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} /></Field>
          </div>
        ) : (
          <span className="lab-muted">Patient visits the centre at the scheduled time.</span>
        )}
      </div>

      <div className="lab-grid cols-2">
        <Field label="Notes (internal)"><textarea className="lab-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <Field label="Things for the patient to remember" hint="Comma separated, e.g. 12h fasting, bring old reports">
          <textarea className="lab-textarea" value={remember} onChange={(e) => setRemember(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
};

export default AppointmentForm;
